import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, X, Upload, Check, Camera, Loader2, Pill, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const TEAL = "#005C66";

// Reusable medicine entry: typeahead + scan + Rx auto-detect + prescription picker.
// Calls onChange({ items, hasRx, selectedRx }) whenever the selection changes.
export const MedicineEntry = ({ userId, onChange }) => {
  const [items, setItems] = useState([{ name: "", quantity: "", isRx: false }]);
  const [hasRx, setHasRx] = useState(false);
  const [rxOptions, setRxOptions] = useState([]);
  const [selectedRx, setSelectedRx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [rxMeds, setRxMeds] = useState([]);
  const [rxMedsLoading, setRxMedsLoading] = useState(false);
  const [rxPick, setRxPick] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [scanningRow, setScanningRow] = useState(null);
  const searchTimer = useRef(null);
  const scanFileRef = useRef(null);
  const scanRowRef = useRef(null);
  const fileRef = useRef(null);

  const loadRxOptions = useCallback(async () => {
    try {
      const res = await api.get(`/patient/${userId}/prescription-options`);
      if (res.data?.success) setRxOptions(res.data.prescriptions || []);
    } catch { /* ignore */ }
  }, [userId]);

  useEffect(() => { loadRxOptions(); }, [loadRxOptions]);

  useEffect(() => {
    onChange?.({ items: items.filter((i) => i.name.trim()), hasRx, selectedRx });
  }, [items, hasRx, selectedRx, onChange]);

  const updateItem = (i, field, val) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const addItem = () => setItems((p) => [...p, { name: "", quantity: "", isRx: false }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const anyRx = items.some((it) => it.isRx);
  useEffect(() => { if (anyRx) setHasRx(true); }, [anyRx]);

  useEffect(() => {
    if (!selectedRx) { setRxMeds([]); setRxPick({}); return; }
    let cancelled = false;
    (async () => {
      setRxMedsLoading(true);
      try {
        const res = await api.get(`/patient/${userId}/prescriptions/${selectedRx}/medicines`, { timeout: 60000 });
        if (cancelled) return;
        const meds = res.data?.medicines || [];
        setRxMeds(meds);
        const init = {};
        meds.forEach((_, idx) => { init[idx] = { checked: true, qty: "" }; });
        setRxPick(init);
      } catch { if (!cancelled) setRxMeds([]); }
      finally { if (!cancelled) setRxMedsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedRx, userId]);

  const toggleRxPick = (idx) => setRxPick((p) => ({ ...p, [idx]: { ...p[idx], checked: !p[idx]?.checked } }));
  const setRxQty = (idx, val) => setRxPick((p) => ({ ...p, [idx]: { ...p[idx], qty: val } }));

  const addRxMedsToOrder = () => {
    const picked = rxMeds
      .map((m, idx) => ({ m, idx }))
      .filter(({ idx }) => rxPick[idx]?.checked)
      .map(({ m, idx }) => ({ name: m.dosage ? `${m.name} ${m.dosage}` : m.name, quantity: rxPick[idx]?.qty || "", isRx: !!m.isRx }));
    if (!picked.length) return;
    setItems((prev) => [...prev.filter((it) => it.name.trim()), ...picked]);
    if (picked.some((p) => p.isRx)) setHasRx(true);
  };

  const onNameChange = (i, val) => {
    setItems((p) => p.map((it, idx) => (idx === i ? { ...it, name: val, isRx: false } : it)));
    setActiveRow(i);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val || val.trim().length < 2) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/medicines/search`, { params: { q: val.trim(), limit: 10 } });
        setSuggestions(res.data?.results || []);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const pickSuggestion = (i, s) => {
    setItems((p) => p.map((it, idx) => (idx === i ? { ...it, name: s.display || s.name, isRx: !!s.isRx } : it)));
    setSuggestions([]);
    setActiveRow(null);
    if (s.isRx) setHasRx(true);
  };

  const onNameBlur = (i) => {
    setTimeout(async () => {
      setActiveRow((cur) => (cur === i ? null : cur));
      setSuggestions([]);
      const name = (items[i]?.name || "").trim();
      if (!name) return;
      try {
        const res = await api.post(`/medicines/classify`, { name });
        if (res.data?.success) {
          const rx = !!res.data.isRx;
          setItems((p) => p.map((it, idx) => (idx === i ? { ...it, isRx: rx } : it)));
          if (rx) setHasRx(true);
        }
      } catch { /* ignore */ }
    }, 180);
  };

  const triggerScan = (i) => { scanRowRef.current = i; scanFileRef.current?.click(); };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onScanFile = async (e) => {
    const file = e.target.files?.[0];
    const i = scanRowRef.current;
    if (!file || i == null) return;
    setScanningRow(i);
    setError("");
    try {
      const base64 = await toBase64(file);
      const res = await api.post(`/medicines/recognize`, { image_b64: base64 }, { timeout: 60000 });
      if (res.data?.success && res.data?.name) {
        const label = res.data.strength ? `${res.data.name} ${res.data.strength}` : res.data.name;
        const rx = !!res.data.isRx;
        setItems((p) => p.map((it, idx) => (idx === i ? { ...it, name: label, isRx: rx } : it)));
        if (rx) setHasRx(true);
      } else setError(res.data?.detail || "Couldn't recognise the medicine. Please type the name.");
    } catch {
      setError("Couldn't analyse the photo. Please try again or type the name.");
    } finally {
      setScanningRow(null);
      if (scanFileRef.current) scanFileRef.current.value = "";
      scanRowRef.current = null;
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const base64 = await toBase64(file);
      const isPdf = file.type.includes("pdf");
      const res = await api.post(`/health/records/upload-base64`, {
        userId, documentType: "prescription",
        documentName: file.name || `Prescription - ${new Date().toLocaleDateString()}`,
        fileType: isPdf ? "pdf" : "image", documentData: base64, isPdf, pageCount: 1,
      }, { timeout: 120000 });
      const newId = res.data?.record?.id || res.data?.recordId || res.data?.id;
      await loadRxOptions();
      if (newId) setSelectedRx(newId);
    } catch {
      setError("Could not upload prescription. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <h2 className="font-bold mt-2 mb-2">Medicines</h2>
      <p className="text-xs text-muted-foreground mb-3">Start typing to pick from suggestions, or scan the pack with your camera.</p>
      <input ref={scanFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScanFile} data-testid="order-scan-file" />
      {items.map((it, i) => (
        <div key={i} className="mb-2 relative">
          <div className="flex items-center gap-2">
            <div className="flex-[2] relative">
              <Input data-testid={`order-item-name-${i}`} placeholder="Medicine name (e.g. Nicip Plus)" value={it.name} autoComplete="off"
                onChange={(e) => onNameChange(i, e.target.value)} onFocus={() => setActiveRow(i)} onBlur={() => onNameBlur(i)} />
              {activeRow === i && suggestions.length > 0 && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-background border rounded-xl shadow-lg max-h-64 overflow-auto" data-testid={`order-suggestions-${i}`}>
                  {suggestions.map((s) => (
                    <button key={s.id} type="button" data-testid={`order-suggestion-${s.id}`} onMouseDown={(e) => { e.preventDefault(); pickSuggestion(i, s); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-accent">
                      <Pill className="h-4 w-4 flex-shrink-0" style={{ color: TEAL }} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">{s.display}</span>
                        {s.composition && <span className="block text-xs text-muted-foreground truncate">{s.composition}</span>}
                      </span>
                      {s.isRx
                        ? <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Rx</span>
                        : <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">OTC</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input data-testid={`order-item-qty-${i}`} className="flex-1" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
            <button type="button" data-testid={`order-item-scan-${i}`} onClick={() => triggerScan(i)} disabled={scanningRow === i} className="h-10 w-10 flex items-center justify-center rounded-xl border flex-shrink-0" style={{ borderColor: "#D1E7E9" }} title="Scan medicine photo">
              {scanningRow === i ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: TEAL }} /> : <Camera className="h-5 w-5" style={{ color: TEAL }} />}
            </button>
            {items.length > 1 && <button data-testid={`order-item-remove-${i}`} onClick={() => removeItem(i)} className="text-red-500 flex-shrink-0"><X className="h-5 w-5" /></button>}
          </div>
          {it.isRx && (
            <div className="flex items-center gap-1 mt-1 ml-1" data-testid={`order-item-rx-badge-${i}`}>
              <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs text-amber-700 font-medium">Prescription required — attach one below</span>
            </div>
          )}
        </div>
      ))}
      <button data-testid="order-add-item" onClick={addItem} className="flex items-center gap-1.5 text-sm font-semibold py-2" style={{ color: TEAL }}><Plus className="h-4 w-4" /> Add another medicine</button>

      <div className="flex items-center gap-3 mt-4">
        <div className="flex-1">
          <h2 className="font-bold">Includes prescription (Schedule-H) medicines</h2>
          <p className="text-xs text-muted-foreground">Required for antibiotics & other Rx-only drugs</p>
        </div>
        <Switch checked={hasRx} onCheckedChange={setHasRx} data-testid="order-rx-toggle" />
      </div>

      {hasRx && (
        <Card className="rounded-2xl p-4 mt-3">
          <p className="text-sm text-muted-foreground mb-3">Attach a prescription (saved to your Health Records)</p>
          {rxOptions.map((o) => (
            <button key={o.id} data-testid={`order-rx-${o.id}`} onClick={() => setSelectedRx(o.id)} className="flex items-center gap-3 w-full py-2 text-left">
              <span className="h-4 w-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: TEAL }}>{selectedRx === o.id && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{o.documentName}</span>
                {o.uploadDate && <span className="block text-xs text-muted-foreground">{new Date(o.uploadDate).toLocaleDateString()}</span>}
              </span>
            </button>
          ))}
          {(rxMedsLoading || rxMeds.length > 0) && (
            <div className="mt-3 border-t pt-3" data-testid="rx-meds-picker">
              <p className="text-sm font-semibold mb-2">Medicines on this prescription</p>
              {rxMedsLoading && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading prescription…</p>}
              {!rxMedsLoading && rxMeds.map((m, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1.5" data-testid={`rx-med-${idx}`}>
                  <button type="button" onClick={() => toggleRxPick(idx)} data-testid={`rx-med-check-${idx}`} className="h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: TEAL, backgroundColor: rxPick[idx]?.checked ? TEAL : "transparent" }}>
                    {rxPick[idx]?.checked && <Check className="h-3.5 w-3.5 text-white" />}
                  </button>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{m.name}{m.dosage ? ` ${m.dosage}` : ""}</span>
                    {(m.frequency || m.duration) && <span className="block text-xs text-muted-foreground truncate">{[m.frequency, m.duration].filter(Boolean).join(" · ")}</span>}
                  </span>
                  {m.isRx
                    ? <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Rx</span>
                    : <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">OTC</span>}
                  <Input data-testid={`rx-med-qty-${idx}`} className="w-20 h-9" placeholder="Qty" value={rxPick[idx]?.qty || ""} onChange={(e) => setRxQty(idx, e.target.value)} />
                </div>
              ))}
              {!rxMedsLoading && rxMeds.length > 0 && (
                <Button variant="outline" className="w-full rounded-xl mt-2" onClick={addRxMedsToOrder} data-testid="rx-meds-add">
                  <Plus className="h-4 w-4 mr-2" style={{ color: TEAL }} /> Add selected to order
                </Button>
              )}
              {!rxMedsLoading && rxMeds.length === 0 && selectedRx && (
                <p className="text-xs text-muted-foreground">No medicines could be read automatically. You can still type them above.</p>
              )}
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="order-rx-file" />
          <Button variant="outline" className="w-full rounded-xl mt-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="order-rx-upload">
            <Upload className="h-4 w-4 mr-2" style={{ color: TEAL }} /> {uploading ? "Uploading…" : "Upload new prescription"}
          </Button>
        </Card>
      )}
      {error && <p className="text-red-600 text-sm mt-2" data-testid="medicine-entry-error">{error}</p>}
    </div>
  );
};
